import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  ArrowRight,
  RefreshCw,
  Mail,
  Calendar,
  Database,
  AlertTriangle,
  ArrowLeft,
  RotateCcw,
  AlertCircle,
} from 'lucide-react';
import ingestionService, { IngestionJob } from '../services/ingestionService';
import employeeService, { Employee } from '../services/employeeService';

const POLL_INTERVAL = 5000; // 5 seconds

// Per-employee sync state: 'idle' | 'syncing' | 'already-running'
type SyncState = 'idle' | 'syncing' | 'already-running';

// Extended Employee with enabledSources
interface EmployeeWithSources extends Employee {
  enabledSources?: string[];
}

const IngestionStatus: React.FC = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<IngestionJob[]>([]);
  const [employees, setEmployees] = useState<Map<string, EmployeeWithSources>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncStates, setSyncStates] = useState<Map<string, SyncState>>(new Map());
  // Track queued sources per employee (from latest startIngestion response)
  const [queuedSources, setQueuedSources] = useState<Map<string, Set<string>>>(new Map());
  const [reauthEmployees, setReauthEmployees] = useState<Set<string>>(new Set());
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadData = useCallback(async () => {
    try {
      const [jobsResult, employeesResult] = await Promise.all([
        ingestionService.listIngestionJobs(50),
        employeeService.listEmployees({ limit: 50 }),
      ]);

      setJobs(jobsResult.jobs || []);

      // Build employee lookup map
      const empMap = new Map<string, EmployeeWithSources>();
      for (const emp of employeesResult.employees || []) {
        empMap.set(emp.employee_id, emp as EmployeeWithSources);
      }
      setEmployees(empMap);

      // Check for calendar reauth errors
      const calendarReauthEmployees = new Set<string>();
      for (const job of jobsResult.jobs || []) {
        if (
          job.sourceType === 'calendar' &&
          job.status === 'failed' &&
          (job as any).errorCode === 'O365_REAUTH_REQUIRED_CALENDAR'
        ) {
          calendarReauthEmployees.add(job.employeeId);
        }
      }
      if (calendarReauthEmployees.size > 0) {
        setReauthEmployees(calendarReauthEmployees);
      }

      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load ingestion status');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load + polling
  useEffect(() => {
    loadData();

    pollRef.current = setInterval(() => {
      loadData();
    }, POLL_INTERVAL);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [loadData]);

  const handleSyncNow = useCallback(async (employeeId: string) => {
    setSyncStates(prev => new Map(prev).set(employeeId, 'syncing'));
    // Clear previous queued state for this employee
    setQueuedSources(prev => {
      const next = new Map(prev);
      next.delete(employeeId);
      return next;
    });

    try {
      const result = await ingestionService.startIngestion([employeeId]);
      // Track any queued sources
      const queued = new Set<string>(
        (result.jobs || [])
          .filter(j => j.status === 'queued' && j.sourceType)
          .map(j => j.sourceType as string)
      );
      if (queued.size > 0) {
        setQueuedSources(prev => new Map(prev).set(employeeId, queued));
      }
      // Kick off an immediate data reload rather than waiting for the next poll interval
      await loadData();
    } catch (err: any) {
      if (err.errorCode === 'INGESTION_IN_PROGRESS') {
        setSyncStates(prev => new Map(prev).set(employeeId, 'already-running'));
        // Clear the transient message after 3 seconds; the job row already shows running status
        setTimeout(() => {
          setSyncStates(prev => new Map(prev).set(employeeId, 'idle'));
        }, 3000);
        // If the active job ID is known, restart polling so it gets picked up
        if (err.activeJobId) await loadData();
      } else {
        setSyncStates(prev => new Map(prev).set(employeeId, 'idle'));
        setError(err.message || 'Failed to start sync');
      }
    } finally {
      setSyncStates(prev => {
        const next = new Map(prev);
        // Only clear syncing state (not 'already-running' — it self-clears after timeout)
        if (next.get(employeeId) === 'syncing') next.set(employeeId, 'idle');
        return next;
      });
    }
  }, [loadData]);

  const handleToggleSource = useCallback(async (employeeId: string, sourceType: string, currentSources: string[]) => {
    const isEnabled = currentSources.includes(sourceType);
    const newSources = isEnabled
      ? currentSources.filter(s => s !== sourceType)
      : [...currentSources, sourceType];

    try {
      await ingestionService.updateEmployeeSources(employeeId, newSources);
      // Update local employee map
      setEmployees(prev => {
        const next = new Map(prev);
        const emp = next.get(employeeId);
        if (emp) {
          next.set(employeeId, { ...emp, enabledSources: newSources });
        }
        return next;
      });
      // Reload to pick up any started jobs
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to update sources');
    }
  }, [loadData]);

  // Stop polling when all jobs are complete
  useEffect(() => {
    const hasActiveJobs = jobs.some(j =>
      j.status === 'pending' || j.status === 'running'
    );
    if (!hasActiveJobs && jobs.length > 0 && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [jobs]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'completed_partial':
        return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'running':
        return <Loader2 className="w-5 h-5 text-niwt-primary animate-spin" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'completed_partial': return 'Partial';
      case 'failed': return 'Failed';
      case 'running': return 'Running';
      default: return 'Pending';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-50 text-green-700';
      case 'completed_partial': return 'bg-amber-50 text-amber-700';
      case 'failed': return 'bg-red-50 text-red-700';
      case 'running': return 'bg-blue-50 text-blue-700';
      default: return 'bg-gray-50 text-gray-600';
    }
  };

  const getProgress = (job: IngestionJob): number => {
    if (job.status === 'completed') return 100;
    if (!job.documentsTotal || job.documentsTotal === 0) return 0;
    return Math.round(((job.documentsProcessed || 0) / job.documentsTotal) * 100);
  };

  const getSourceIcon = (sourceType: string) => {
    if (sourceType === 'calendar') {
      return <Calendar className="w-3 h-3 mr-1" />;
    }
    return <Mail className="w-3 h-3 mr-1" />;
  };

  const allComplete = jobs.length > 0 && jobs.every(j =>
    j.status === 'completed' || j.status === 'completed_partial' || j.status === 'failed'
  );

  const hasSuccessfulJobs = jobs.some(j =>
    j.status === 'completed' || j.status === 'completed_partial'
  );

  // Group jobs by employee (latest job per employee)
  const jobsByEmployee = new Map<string, IngestionJob[]>();
  for (const job of jobs) {
    const existing = jobsByEmployee.get(job.employeeId) || [];
    existing.push(job);
    jobsByEmployee.set(job.employeeId, existing);
  }

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-niwt-primary mx-auto mb-4" />
          <p className="text-gray-600">Loading ingestion status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-libre">Ingestion Status</h1>
          <p className="text-gray-600 mt-1">
            Monitor the progress of data ingestion for your selected employees
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/employee-selection')}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Select Employees
          </button>
          <button
            onClick={loadData}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4 mr-1.5" />
            Refresh
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Calendar reauth alert */}
      {reauthEmployees.size > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-amber-800 font-medium">Calendar access requires reconnecting Microsoft 365</p>
            <p className="text-xs text-amber-700 mt-1">
              The Calendars.Read permission is missing. Reconnect your Microsoft 365 account to enable calendar ingestion.
            </p>
          </div>
          <button
            onClick={() => navigate('/azure-setup')}
            className="px-3 py-1.5 text-xs font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors whitespace-nowrap"
          >
            Reconnect
          </button>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total Jobs</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{jobs.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Running</p>
          <p className="text-2xl font-bold text-blue-600 mt-1">
            {jobs.filter(j => j.status === 'running').length}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Completed</p>
          <p className="text-2xl font-bold text-green-600 mt-1">
            {jobs.filter(j => j.status === 'completed' || j.status === 'completed_partial').length}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Vectors Created</p>
          <p className="text-2xl font-bold text-niwt-primary mt-1">
            {jobs.reduce((sum, j) => sum + (j.vectorsCreated || 0), 0).toLocaleString()}
          </p>
        </div>
      </div>

      {/* Job list */}
      {jobs.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <Database className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No ingestion jobs yet</h3>
          <p className="text-gray-500 mb-4">Select employees to start ingesting their data</p>
          <button
            onClick={() => navigate('/employee-selection')}
            className="inline-flex items-center px-4 py-2 bg-niwt-primary text-white font-medium text-sm rounded-lg hover:bg-niwt-secondary transition-colors"
          >
            Select Employees
            <ArrowRight className="w-4 h-4 ml-2" />
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {Array.from(jobsByEmployee.entries()).map(([employeeId, employeeJobs]) => {
            const employee = employees.get(employeeId);
            const enabledSources: string[] = (employee as any)?.enabledSources || ['email'];
            const empQueuedSources = queuedSources.get(employeeId) || new Set<string>();
            const hasReauthError = reauthEmployees.has(employeeId);

            // Show latest job first
            const sortedJobs = [...employeeJobs].sort(
              (a, b) => (b.startedAt || '').localeCompare(a.startedAt || '')
            );
            const latestJob = sortedJobs[0];

            return (
              <div key={employeeId} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {/* Employee header */}
                <div className="p-4 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                        <Mail className="w-5 h-5 text-niwt-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {employee?.displayName || employeeId}
                        </p>
                        <p className="text-xs text-gray-500">{employee?.email || ''}</p>
                        {latestJob.completedAt && latestJob.status !== 'failed' && (
                          <p className="text-xs text-gray-400 mt-0.5">
                            Last sync: {new Date(latestJob.completedAt).toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(latestJob.status)}
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(latestJob.status)}`}>
                        {getStatusLabel(latestJob.status)}
                      </span>
                      {/* Sync Now — disabled while a job is active */}
                      {(() => {
                        const isActive = latestJob.status === 'pending' || latestJob.status === 'running';
                        const syncState = syncStates.get(employeeId) ?? 'idle';
                        const isSyncing = syncState === 'syncing';
                        const alreadyRunning = syncState === 'already-running';
                        return (
                          <button
                            onClick={() => handleSyncNow(employeeId)}
                            disabled={isActive || isSyncing}
                            title={isActive ? 'Sync already in progress' : 'Start incremental sync'}
                            className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-lg border transition-colors ${
                              isActive || isSyncing
                                ? 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed'
                                : alreadyRunning
                                ? 'border-amber-200 text-amber-700 bg-amber-50'
                                : 'border-gray-300 text-gray-600 bg-white hover:bg-gray-50 hover:border-gray-400'
                            }`}
                          >
                            {isSyncing ? (
                              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            ) : (
                              <RotateCcw className="w-3 h-3 mr-1" />
                            )}
                            {isSyncing ? 'Starting…' : alreadyRunning ? 'Already running' : 'Sync now'}
                          </button>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Per-employee source chips */}
                  <div className="flex items-center space-x-2 mt-3">
                    {['email', 'calendar'].map(sourceType => {
                      const isEnabled = enabledSources.includes(sourceType);
                      const isQueued = empQueuedSources.has(sourceType);
                      const sourceLabel = sourceType === 'calendar' ? 'Calendar' : 'Email';
                      const SourceIcon = sourceType === 'calendar' ? Calendar : Mail;

                      if (isQueued) {
                        return (
                          <span
                            key={sourceType}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200"
                            title={`${sourceLabel} is queued — will start after current job completes`}
                          >
                            <SourceIcon className="w-3 h-3 mr-1" />
                            {sourceLabel} — queued
                          </span>
                        );
                      }

                      return (
                        <button
                          key={sourceType}
                          onClick={() => handleToggleSource(employeeId, sourceType, enabledSources)}
                          title={isEnabled ? `Disable ${sourceLabel} ingestion` : `Enable ${sourceLabel} ingestion`}
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border transition-colors ${
                            isEnabled
                              ? 'bg-niwt-primary/10 text-niwt-primary border-niwt-primary/30 hover:bg-niwt-primary/20'
                              : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <SourceIcon className="w-3 h-3 mr-1" />
                          {sourceLabel}
                          {isEnabled ? (
                            <CheckCircle className="w-3 h-3 ml-1" />
                          ) : (
                            <span className="ml-1 text-gray-400">○</span>
                          )}
                        </button>
                      );
                    })}

                    {hasReauthError && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        Calendar needs reconnect
                      </span>
                    )}
                  </div>
                </div>

                {/* Job details */}
                {sortedJobs.map(job => {
                  const progress = getProgress(job);

                  return (
                    <div key={job.jobId} className="p-4 border-b border-gray-50 last:border-b-0">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          {/* Source type badge */}
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            job.sourceType === 'calendar'
                              ? 'bg-purple-50 text-purple-700'
                              : 'bg-blue-50 text-blue-700'
                          }`}>
                            {getSourceIcon(job.sourceType || 'email')}
                            {job.sourceType === 'calendar' ? 'Calendar' : 'Email'}
                          </span>
                          <span className="text-xs font-medium text-gray-500 uppercase">
                            {job.jobType}
                          </span>
                          {job.isPartial && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700">
                              Continuation
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                          {job.documentsProcessed !== undefined && (
                            <span>{job.documentsProcessed} {job.sourceType === 'calendar' ? 'events' : 'docs'} processed</span>
                          )}
                          {job.vectorsCreated !== undefined && (
                            <span>{job.vectorsCreated} vectors</span>
                          )}
                          {job.errorsCount !== undefined && job.errorsCount > 0 && (
                            <span className="text-red-600">{job.errorsCount} errors</span>
                          )}
                        </div>
                      </div>

                      {/* Progress bar */}
                      {(job.status === 'running' || job.status === 'completed' || job.status === 'completed_partial') && (
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all duration-500 ${
                              job.status === 'completed'
                                ? 'bg-green-500'
                                : job.status === 'completed_partial'
                                ? 'bg-amber-500'
                                : 'bg-niwt-primary'
                            }`}
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      )}

                      {/* Timestamps */}
                      <div className="flex items-center space-x-4 mt-2 text-xs text-gray-400">
                        {job.startedAt && (
                          <span>Started: {new Date(job.startedAt).toLocaleTimeString()}</span>
                        )}
                        {job.completedAt && (
                          <span>Completed: {new Date(job.completedAt).toLocaleTimeString()}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {/* Bottom action */}
      {allComplete && hasSuccessfulJobs && (
        <div className="bg-green-50 rounded-xl border border-green-200 p-6 text-center">
          <CheckCircle className="w-10 h-10 text-green-600 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Ingestion Complete</h3>
          <p className="text-sm text-gray-600 mb-4">
            Your digital twins are ready. Start chatting with AI-powered assistance.
          </p>
          <button
            onClick={() => navigate('/chat')}
            className="inline-flex items-center px-6 py-3 bg-niwt-primary text-white font-medium rounded-lg hover:bg-niwt-secondary transition-colors"
          >
            Continue to Chat
            <ArrowRight className="w-4 h-4 ml-2" />
          </button>
        </div>
      )}
    </div>
  );
};

export default IngestionStatus;
